import type { AuthTokenResponse } from '@yescapa-dev/ysc-api-js/legacy'
import type { APIToken } from '@yescapa-dev/ysc-api-js/modern'
import { parse, serialize } from 'cookie-es'
import type { CookieSerializeOptions } from 'cookie-es'
import { setCookie, getCookie } from 'h3'
import { ACCESS_TOKEN, defaultCookieOptions, REFRESH_ACCESS_TOKEN, REFRESH_ACCESS_TOKEN_VALIDITY } from '~/constants/auth'

export const useYscTokens = () => {
  const evt = useRequestEvent()

  // read
  const readCookies = (name: string) => {
    if (import.meta.server && isDefined(evt)) {
      // only when a refresh tokens was sent server side
      if (isDefined(evt.context._ysc_cookie?.[name])) {
        return evt.context._ysc_cookie[name]
      }
      return getCookie(evt, name)
    }
    else if (import.meta.client) {
      return parse(document.cookie)?.[name]
    }
  }

  const readAccessToken = () => {
    return readCookies(ACCESS_TOKEN)
  }

  const readRefreshAccessToken = () => {
    return readCookies(REFRESH_ACCESS_TOKEN)
  }

  // write
  const writeCookie = (name: string, value: string, opts: CookieSerializeOptions) => {
    if (import.meta.server && isDefined(evt)) {
      setCookie(evt, name, value, opts)
      if (!isDefined(evt.context._ysc_cookie)) {
        evt.context._ysc_cookie = {}
      }

      if (value.length === 0 || (isDefined(opts.maxAge) && opts.maxAge <= 0)) {
        evt.context._ysc_cookie[name] = undefined
      }
      else {
        evt.context._ysc_cookie[name] = value
      }
    }
    else if (import.meta.client) {
      document.cookie = serialize(name, value, opts)
    }
  }

  const writeTokens = (tokens: APIToken | AuthTokenResponse) => {
    writeCookie(ACCESS_TOKEN,
      tokens.access_token,
      {
        ...defaultCookieOptions,
        maxAge: tokens.expires_in,
      },
    )

    writeCookie(REFRESH_ACCESS_TOKEN,
      tokens.refresh_token,
      {
        ...defaultCookieOptions,
        maxAge: REFRESH_ACCESS_TOKEN_VALIDITY,
      },
    )
  }

  // delete
  const removeTokens = () => {
    writeCookie(ACCESS_TOKEN,
      '',
      {
        ...defaultCookieOptions,
        maxAge: -1,
      },
    )
    writeCookie(REFRESH_ACCESS_TOKEN,
      '',
      {
        ...defaultCookieOptions,
        maxAge: -1,
      },
    )
  }

  return {
    readAccessToken,
    readRefreshAccessToken,
    writeTokens,
    removeTokens,
  }
}
